import { Input, InputNumber, Popover } from "antd";
import { type FC, useEffect, useMemo, useState } from "react";
import { Button } from "syngenta-digital-cropwise-react-ui-kit";
import { useNewTrialState } from "../../../../../pages/new-trail/hooks/NewTrialStateProvider";
import { restrictToTwoDecimals } from "../../../../../pages/create-protocol/utils";
import { usePlotCardState } from "../../utils";

interface AbLinePopupProps {
  children: React.ReactNode;
  open: boolean;
  abLineId: number | null;
  onClose: () => void;
}

type CoordinatePair = [number | undefined, number | undefined];

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value === "number") {
    return Number.isNaN(value) ? undefined : value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  return undefined;
};

const normalizeCoordinatePairs = (coordinates: unknown): CoordinatePair[] => {
  if (Array.isArray(coordinates) && Array.isArray(coordinates[0])) {
    const a = coordinates[0] as unknown[];
    const b = (
      Array.isArray(coordinates[1]) ? coordinates[1] : coordinates[0]
    ) as unknown[];

    return [
      [toNumberOrUndefined(a[0]), toNumberOrUndefined(a[1])],
      [toNumberOrUndefined(b[0]), toNumberOrUndefined(b[1])],
    ];
  }

  if (Array.isArray(coordinates) && coordinates.length >= 2) {
    const a0 = toNumberOrUndefined(coordinates[0]);
    const a1 = toNumberOrUndefined(coordinates[1]);
    return [
      [a0, a1],
      [a0, a1],
    ];
  }

  return [
    [undefined, undefined],
    [undefined, undefined],
  ];
};

const formatCoordinateInput = (
  prefix: "Lat" | "Long",
  value: string | number | undefined
): string => {
  if (value === undefined || value === null || value === "") {
    return `${prefix} `;
  }

  return `${prefix} ${value}`;
};

const parseCoordinateInput = (raw: string | undefined): number => {
  if (!raw) {
    return Number.NaN;
  }

  const cleaned = raw
    .replace(/^(Lat|Long)\s*/i, "")
    .replace(/[^0-9+\-.]/g, "")
    .trim();

  if (!cleaned) {
    return Number.NaN;
  }

  return Number(cleaned);
};

export const AbLinePopup: FC<AbLinePopupProps> = ({
  children,
  open,
  abLineId,
  onClose,
}) => {
  const { newTrialState, setNewTrialState } = useNewTrialState();
  const { handleMachineGridData, getAbLineColor } = usePlotCardState();

  const abLineIndex = useMemo(() => {
    if (abLineId == null) {
      return -1;
    }

    return newTrialState.ab_line.findIndex((line) => line.id === abLineId);
  }, [abLineId, newTrialState.ab_line]);

  const selectedAbLine =
    abLineIndex >= 0 ? newTrialState.ab_line[abLineIndex] : undefined;
  const [aPosition, bPosition] = normalizeCoordinatePairs(
    selectedAbLine?.geojson?.geometry?.coordinates
  );

  const [draftName, setDraftName] = useState<string>("");
  const [draftWidth, setDraftWidth] = useState<number | null>(null);
  const [draftCoordinates, setDraftCoordinates] = useState<CoordinatePair[]>([
    [undefined, undefined],
    [undefined, undefined],
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const currentWidth =
      selectedAbLine?.grid_width ?? selectedAbLine?.gridWidth ?? null;

    setDraftName(selectedAbLine?.name ?? "");
    setDraftWidth(typeof currentWidth === "number" ? currentWidth : null);
    setDraftCoordinates([
      [aPosition[0], aPosition[1]],
      [bPosition[0], bPosition[1]],
    ]);
  }, [
    open,
    selectedAbLine?.id,
    selectedAbLine?.name,
    selectedAbLine?.grid_width,
    selectedAbLine?.gridWidth,
    aPosition[0],
    aPosition[1],
    bPosition[0],
    bPosition[1],
  ]);

  const updateCurrentAbLine = (updater: (line: any) => any): void => {
    setNewTrialState((prevState: any) => {
      const index = prevState.ab_line.findIndex(
        (line: any) => line.id === abLineId
      );
      if (index < 0) {
        return prevState;
      }

      const nextAbLine = [...prevState.ab_line];
      nextAbLine[index] = updater(nextAbLine[index]);

      return {
        ...prevState,
        ab_line: nextAbLine,
      };
    });
  };

  const updateDraftCoordinate = (
    pointIndex: 0 | 1,
    axisIndex: 0 | 1,
    value: number | null
  ): void => {
    setDraftCoordinates((previousCoordinates) => {
      const nextCoordinates: CoordinatePair[] = [
        [...previousCoordinates[0]] as CoordinatePair,
        [...previousCoordinates[1]] as CoordinatePair,
      ];

      nextCoordinates[pointIndex][axisIndex] = value ?? undefined;

      return nextCoordinates;
    });
  };

  const applyChanges = (): void => {
    if (abLineIndex < 0) {
      onClose();
      return;
    }

    const isValidWidth =
      draftWidth !== null && !Number.isNaN(draftWidth) && draftWidth > 0;

    let nextGridWidth: number | undefined;
    if (draftWidth === null) {
      nextGridWidth = undefined;
    } else if (isValidWidth) {
      nextGridWidth = draftWidth;
    } else {
      nextGridWidth = selectedAbLine?.grid_width ?? selectedAbLine?.gridWidth;
    }

    updateCurrentAbLine((line: any) => ({
      ...line,
      key: "A/B Line + Machinery Grid",
      visible: true,
      name: draftName,
      geojson: {
        ...line.geojson,
        geometry: {
          ...line.geojson?.geometry,
          coordinates: draftCoordinates,
        },
      },
      grid_width: nextGridWidth,
      gridWidth: nextGridWidth,
    }));

    if (isValidWidth) {
      handleMachineGridData(draftWidth, abLineIndex);
    }

    onClose();
  };

  const titleContent = <div className="ab-line-popup-title">A/B Line</div>;

  const content = (
    <div className="ab-line-popup-content">
      <div className="ab-line-top-row-wrap">
        <span
          className="ab-line-anchor-dot"
          aria-hidden="true"
          style={{
            backgroundColor: abLineId
              ? getAbLineColor(abLineId, "hexCode")
              : undefined,
          }}
        />
        <div className="ab-line-top-row">
          <div className="ab-line-field">
            <label htmlFor="ab-line-name">Name</label>
            <div className="ab-line-input-main">
              <Input
                id="ab-line-name"
                value={draftName}
                onChange={(event) => {
                  setDraftName(event.target.value);
                }}
              />
            </div>
          </div>

          <div className="ab-line-field">
            <label htmlFor="ab-line-grid-width">Grid Width</label>
            <div className="ab-line-input-main">
              <InputNumber
                id="ab-line-grid-width"
                min={0.01}
                precision={2}
                step={0.01}
                controls={false}
                suffix="(m)"
                value={draftWidth}
                onChange={(value) => {
                  setDraftWidth(value);
                }}
                onKeyDown={restrictToTwoDecimals}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="ab-line-position-row">
        <div className="ab-line-field">
          <label htmlFor="ab-line-a-position-long">A Position</label>
          <div className="ab-line-coordinates">
            <InputNumber
              id="ab-line-a-position-long"
              controls={false}
              formatter={(value) => {
                return formatCoordinateInput("Long", value);
              }}
              parser={parseCoordinateInput}
              value={draftCoordinates[0]?.[0] ?? null}
              onChange={(value) => {
                updateDraftCoordinate(0, 0, value);
              }}
            />
            <InputNumber
              aria-label="A Position Latitude"
              controls={false}
              formatter={(value) => {
                return formatCoordinateInput("Lat", value);
              }}
              parser={parseCoordinateInput}
              value={draftCoordinates[0]?.[1] ?? null}
              onChange={(value) => {
                updateDraftCoordinate(0, 1, value);
              }}
            />
          </div>
        </div>

        <div className="ab-line-field">
          <label htmlFor="ab-line-b-position-long">B Position</label>
          <div className="ab-line-coordinates">
            <InputNumber
              id="ab-line-b-position-long"
              controls={false}
              formatter={(value) => {
                return formatCoordinateInput("Long", value);
              }}
              parser={parseCoordinateInput}
              value={draftCoordinates[1]?.[0] ?? null}
              onChange={(value) => {
                updateDraftCoordinate(1, 0, value);
              }}
            />
            <InputNumber
              aria-label="B Position Latitude"
              controls={false}
              formatter={(value) => {
                return formatCoordinateInput("Lat", value);
              }}
              parser={parseCoordinateInput}
              value={draftCoordinates[1]?.[1] ?? null}
              onChange={(value) => {
                updateDraftCoordinate(1, 1, value);
              }}
            />
          </div>
        </div>
      </div>

      <Button className="ab-line-apply-btn" onClick={applyChanges}>
        Apply
      </Button>
    </div>
  );

  return (
    <Popover
      content={content}
      title={titleContent}
      placement="right"
      trigger="click"
      open={open}
      autoAdjustOverflow={false}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      {children}
    </Popover>
  );
};
